import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Modal, Text, Button } from '@shopify/polaris';
import { useProgramActions, useProgramStore } from '#client/stores/program/useProgramStore';
import { useToast } from '#client/components/common/Toast/ToastContext';

const ProgramArchiveModal = () => {
    const navigate = useNavigate();

    // Zustand store hooks
    const programId = useProgramStore.use.programId();
    const errors = useProgramStore.use.errors();
    const { archiveProgram } = useProgramActions();

    const { addToast } = useToast();

    const [archiveModalOpen, setArchiveModalOpen] = useState(false);

    const handleChange = useCallback(() => setArchiveModalOpen(!archiveModalOpen), [archiveModalOpen]);

    const handleArchive = async () => {
        try {
            const response = await archiveProgram(programId);

            if (response.success) {
                // Close modal
                setArchiveModalOpen(false);
                // and navigate to program view
                navigate(`/program-management/view/${programId}`, { viewTransition: true });
                // scrroll to top
                window.scrollTo({ top: 0, behavior: 'smooth' });
                // Set success message
                addToast({
                    type: 'success',
                    message: 'Program successfully archived',
                });
            } else {
                addToast({
                    type: 'error',
                    message: response.message || 'Failed to archive program',
                });
            }
        } catch (error) {
            const message = error?.response?.data?.message || error?.response?.data?.error || error.message || 'Failed to archive program';
            addToast({
                type: 'error',
                message,
            });
        }
    };

    const activator = (
        <Button variant="plain" tone="critical" onClick={() => setArchiveModalOpen(true)} error={errors.archive}>
            Archive Program
        </Button>
    );

    return (
        <>
            <Modal
                activator={activator}
                open={archiveModalOpen}
                onClose={handleChange}
                title="WARNING: About to Archive Program"
                primaryAction={{
                    content: 'Yes',
                    onAction: handleArchive,
                    destructive: true,
                }}
                secondaryActions={[
                    {
                        content: 'No',
                        onAction: () => {
                            setArchiveModalOpen(false);
                        },
                    },
                ]}
            >
                <Modal.Section>
                    <Text as="p">This is permanent and irreversible. Are you sure you want to archive this program?</Text>
                    <Text as="p">
                        <strong>Note:</strong> This will result in all outstanding vouchers being immediately revoked.
                    </Text>
                </Modal.Section>
            </Modal>
        </>
    );
};

export default ProgramArchiveModal;
